import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import {
  MDBCol,
  MDBRow,
} from "mdbreact";
import { CentralStore } from "../../stores/erros/CentralStore";
import {
  Chart as BizChart,
  Coordinate,
  Axis,
  Tooltip,
  Interval,
  Legend,
  Annotation,
} from "bizcharts";

import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { IEmpresa, newEmpresa, newISomaNotas } from "../../model";
import { Alignment, Button, FormGroup } from "@blueprintjs/core";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { formatCnpj, formaterMesAbreviado } from "../../utils";
import { Chart } from '@antv/g2';

interface State {
  open_alert: boolean
}

@observer
export default class FaixasPage extends React.Component<{}, State> {

  obs = observable({
    selectedEmpresa: newEmpresa() as IEmpresa
  })

  constructor(props: {}) {
    super(props);

    this.state = {
      open_alert: false
    } 

  }

  private g2ContainerRef = React.createRef<HTMLDivElement>();
  private g2Chart?: Chart;

  private buildG2Data = () => {
    return this.getValoresMensais("Saída", "saida");
  };

  private renderOrUpdateG2 = () => {
    const container = this.g2ContainerRef.current;
    if (!container) return;

    const data = this.buildG2Data();

    const formatBRL = (v: any) =>
      new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);


    if (!this.g2Chart) {
      this.g2Chart = new Chart({
        container,
        autoFit: true,
        height: 500,
      });
    }

    (this.g2Chart as any).options({
      type: "interval",
      autoFit: true,
      data,
      legend: false,
      encode: {
        x: "Mes",
        y: "Valor",
        color: "tipo",
        series: "tipo"
      },
      tooltip: {
        title: { field: "Mes", valueFormatter: (v: any) => formaterMesAbreviado(v) },
        items: [
          {
            field: "Valor",
            valueFormatter: (v: any) => formatBRL(v),
          },
        ],
      },
      transform: [{ type: "dodgeX" }],
      scale: { y: { nice: true } },
      axis: {
        y: {
          labelFormatter: (v: any) => formatBRL(v),

          grid: true,
          gridLineWidth: 1.5,
          gridStrokeOpacity: 0.35,

          line: true,
          lineLineWidth: 1.5,

          tick: true,
          tickLineWidth: 1.5,
          tickStrokeOpacity: 0.6,
        },
        x: {
          title: false,
          labelFormatter: (x: any) => formaterMesAbreviado(x),
        }
      },

    });

    this.g2Chart.render();
  };

  componentWillUnmount(): void {
    CentralStore.obs.soma_notas_empresa = newISomaNotas();
    this.obs.selectedEmpresa = newEmpresa();

    this.g2Chart?.destroy();
    this.g2Chart = undefined;
  }

  componentDidMount(): void {
    this.renderOrUpdateG2();
  }

  componentDidUpdate(): void {
    this.renderOrUpdateG2();
  }

  async handleBusca(empresa: IEmpresa) {
    if (!empresa?.cnpj) return;

    try {
      await CentralStore.getFaturamentoEmpresa(empresa.cnpj);
    } catch (e) {
      console.error(e);
    }
  }

  async handleSelectEmpresa(empresa) {

    this.obs.selectedEmpresa = empresa

    CentralStore.obs.soma_notas_empresa = newISomaNotas()

    this.handleBusca(empresa)

  }

  getValoresMensais = (tipo, chaveBase) => {
    const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

    return Array.from({ length: 12 }, (_, i) => ({
      tipo,
      Valor: Number(CentralStore.obs.soma_notas_empresa[`${chaveBase}_${i + 1}`] || 0),
      Mes: CentralStore.obs.soma_notas_empresa[`label_${i + 1}`] || meses[i],
    }));
  };

  totais_faixa_simples(total12m: number) {
    const limite_simples = 4_800_000;

    const usado = Math.max(0, Math.min(total12m || 0, limite_simples));
    const restante = Math.max(0, limite_simples - usado);

    const percent = limite_simples > 0 ? (total12m / limite_simples) * 100 : 0;
    const percentLabel = `${percent.toFixed(1)}%`;

    let title = "Usou";
    let texto = "da faixa do Simples Nacional";

    if (total12m <= 0) {
      title = "";
      texto = "Sem faturamento no período";
    } else if (total12m > limite_simples) {
      title = "Atenção";
      texto = "Acima do limite da faixa do Simples Nacional";
    }

    const data =
      limite_simples > 0
        ? [
          { tipo: "Usado", value: usado },
          { tipo: "Restante", value: restante },
        ]
        : [];

    const formatBRL = (v: any) =>
      new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);

    const scale = {
      value: {
        formatter: (val) => formatBRL(val),
      },
    };

    let legendMap: any = {};
    let chartIns: any;

    return (
      <BizChart
        data={data}
        height={500}
        autoFit
        scale={scale}
        interactions={["element-active"]}
        onGetG2Instance={(c) => (chartIns = c)}
      >
        <Coordinate type="theta" radius={0.8} innerRadius={0.85} />
        <Axis visible={false} />
        <Tooltip showTitle={false} />

        <Interval
          adjust="stack"
          position="value"
          color={[
            "tipo",
            (t) => (t === "Usado" ? "#5b96f1" : "#e0e0e0"),
          ]}
          size={16}
          tooltip={[
            "tipo*value",
            (tipo, value) => ({ name: tipo, value: formatBRL(value) }),
          ]}
        />

        <Annotation.Text
          position={["50%", "34%"]}
          content={title}
          style={{
            fontSize: 14,
            fill: "#999",
            textAlign: "center",
            textBaseline: "middle",
          }}
        />

        <Annotation.Text
          position={["50%", "45%"]}
          content={percentLabel}
          style={{
            fontSize: 30,
            fontWeight: "bold",
            textAlign: "center",
            textBaseline: "middle",
            fill: total12m > limite_simples ? "#d32f2f" : "#111",
          }}
        />

        <Annotation.Text
          position={["50%", "58%"]}
          content={`${formatBRL(total12m)} / ${formatBRL(limite_simples)}`}
          style={{
            fontSize: 12,
            fill: "#999",
            textAlign: "center",
            textBaseline: "middle",
          }}
        />

        <Annotation.Text
          position={["50%", "65%"]}
          content={texto}
          style={{
            fontSize: 12,
            fill: "#999",
            textAlign: "center",
            textBaseline: "middle",
          }}
        />

        <Legend
          custom={true}
          items={[
            { name: "Usado", value: "Usado", marker: { symbol: "circle", style: { fill: "#5b96f1" } } },
            { name: "Restante", value: "Restante", marker: { symbol: "circle", style: { fill: "#e0e0e0" } } },
          ]}
          onChange={(ev) => {
            const { item } = ev;
            const { value } = item;
            const checked = !item.unchecked;
            legendMap[value] = checked;

            chartIns.filter("tipo", (val) => legendMap[val] !== false);
            chartIns.render(true);
          }}
        />
      </BizChart>
    );
  }

  render() {

    const dataSaida = this.getValoresMensais("Saída", "saida");
    const totalSaida = dataSaida.reduce((acc, curr) => acc + (Number(curr.Valor) || 0), 0);

    let empresaText = this.obs.selectedEmpresa.razao_social != ""
      ? `${this.obs.selectedEmpresa.razao_social} (${formatCnpj(this.obs.selectedEmpresa.cnpj)})`
      : "Selecione uma empresa";

    const total12m = Number(CentralStore.obs.soma_notas_empresa?.total_12m || totalSaida || 0);

    return (
      <>

        <div style={{ display: "flex" }}>
          <FormGroup className="empresaSelect" label="Empresa" style={{ width: 500, marginRight: 15 }}>
            <EmpresaSelect
              items={EmpresaStore.sortedEmpresas.filter(x => { return x.simples_nacional })}
              itemRenderer={renderEmpresa}
              itemPredicate={filterEmpresa}
              onItemSelect={(e: IEmpresa) => this.handleSelectEmpresa(e)}
              inputProps={{ placeholder: "Filtrar uma empresa" }}
            >
              <Button
                text={empresaText}
                rightIcon="double-caret-vertical"
                alignText={Alignment.LEFT} />
            </EmpresaSelect>
          </FormGroup>

        </div>

        <MDBRow>
          <MDBCol md="12">
            <MDBRow>

              <div className="container-empresas">
                <div className="blocoProcesso">
                  <h3 className="titleProccess">Faturamento por mês dos últimos 12 meses</h3>

                  <div style={{ width: "100%", marginTop: 10 }}>
                    <div ref={this.g2ContainerRef} />
                  </div>
                </div>
              </div>

              <div className="container-empresas-totais">
                <div className="blocoProcesso">

                  <h3 className="titleProccess">Total faturamento da faixa do simples nacional</h3>
                  <br />
                  <br />
                  {this.totais_faixa_simples(total12m)}
                  <br />
                </div>
              </div>

            </MDBRow>
          </MDBCol>
        </MDBRow>

      </>

    )

  }
}
